import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/context/LanguageContext";
import { CompareProvider } from "@/context/CompareContext";
import { ROUTER_BASENAME } from "@/i18n/locale";

import Header from "@/components/Header";
import AuctionHeaderRibbon from "@/components/AuctionHeaderRibbon";
import Footer from "@/components/Footer";

import HomePage from "@/pages/HomePage";
import AppsPage from "@/pages/AppsPage";
import AppDetailPage from "@/pages/AppDetailPage";
import ComparePage from "@/pages/ComparePage";
import RankingsPage from "@/pages/RankingsPage";
import AdvisorPage from "@/pages/AdvisorPage";
import SponsorPage from "@/pages/SponsorPage";
import BestHorseAppsPage from "@/pages/BestHorseAppsPage";
import VersusPage from "@/pages/VersusPage";
import AdvertSubmitPage from "@/pages/AdvertSubmitPage";
import BillingPortalPage from "@/pages/BillingPortalPage";
import BestOfPage from "@/pages/BestOfPage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import ClaimEditorPage from "@/pages/ClaimEditorPage";
import BlogGeneratePage from "@/pages/BlogGeneratePage";
import BlogQueuePage from "@/pages/BlogQueuePage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AppFeaturePage from "@/pages/AppFeaturePage";
import SubmitAppPage from "@/pages/SubmitAppPage";
import PremiumListingPage from "@/pages/PremiumListingPage";
import ContactPage from "@/pages/ContactPage";
import AuctionPage from "@/pages/AuctionPage";
import BidderDashboardPage from "@/pages/BidderDashboardPage";
import HorseWeightCalculatorPage from "@/pages/HorseWeightCalculatorPage";
import HayRationCalculatorPage from "@/pages/HayRationCalculatorPage";
import ColicSymptomCheckerPage from "@/pages/ColicSymptomCheckerPage";
import FoalingDateCalculatorPage from "@/pages/FoalingDateCalculatorPage";
import HorseAgeCalculatorPage from "@/pages/HorseAgeCalculatorPage";
import HorseCostCalculatorPage from "@/pages/HorseCostCalculatorPage";
import FarrierScheduleCalculatorPage from "@/pages/FarrierScheduleCalculatorPage";
import TrailerWeightCalculatorPage from "@/pages/TrailerWeightCalculatorPage";
import ToolSharePage from "@/pages/ToolSharePage";
import TopSharedPage from "@/pages/TopSharedPage";
import ClaimAppPage from "@/pages/ClaimAppPage";
import PublisherDashboardPage from "@/pages/PublisherDashboardPage";
import PublisherLoginPage from "@/pages/PublisherLoginPage";
import PublisherVerifyPage from "@/pages/PublisherVerifyPage";
import { CategoriesIndexPage, CategoryDetailPage } from "@/pages/CategoryPages";
import BestOfCategoryPage from "@/pages/BestOfCategoryPage";
import OwnerBanner from "@/components/OwnerBanner";

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <CompareProvider>
          <BrowserRouter basename={ROUTER_BASENAME}>
            <OwnerBanner />
            <AuctionHeaderRibbon />
            <Header />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/apps" element={<AppsPage />} />
              <Route path="/apps/:slug" element={<AppDetailPage />} />
              <Route path="/apps/:slug/feature/:label" element={<AppFeaturePage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/rankings" element={<RankingsPage />} />
              <Route path="/categories" element={<CategoriesIndexPage />} />
              <Route path="/categories/:key" element={<CategoryDetailPage />} />
              <Route path="/best-of/:category" element={<BestOfCategoryPage />} />
              <Route path="/advisor" element={<AdvisorPage />} />
              <Route path="/sponsor" element={<SponsorPage />} />
              <Route path="/submit-app" element={<SubmitAppPage />} />
              <Route path="/list-your-app" element={<SubmitAppPage />} />
              <Route path="/premium-listing" element={<PremiumListingPage />} />
              <Route path="/premium" element={<PremiumListingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/auction" element={<AuctionPage />} />
              <Route path="/auction/dashboard" element={<BidderDashboardPage />} />
              <Route path="/horse-weight-calculator" element={<HorseWeightCalculatorPage />} />
              <Route path="/paardengewicht-calculator" element={<HorseWeightCalculatorPage />} />
              <Route path="/tools/weight" element={<HorseWeightCalculatorPage />} />
              <Route path="/hay-ration-calculator" element={<HayRationCalculatorPage />} />
              <Route path="/hooi-rantsoen-calculator" element={<HayRationCalculatorPage />} />
              <Route path="/colic-symptom-checker" element={<ColicSymptomCheckerPage />} />
              <Route path="/koliek-checker" element={<ColicSymptomCheckerPage />} />
              <Route path="/foaling-date-calculator" element={<FoalingDateCalculatorPage />} />
              <Route path="/veulendatum-calculator" element={<FoalingDateCalculatorPage />} />
              <Route path="/horse-age-calculator" element={<HorseAgeCalculatorPage />} />
              <Route path="/paardenleeftijd-calculator" element={<HorseAgeCalculatorPage />} />
              <Route path="/horse-cost-calculator" element={<HorseCostCalculatorPage />} />
              <Route path="/paardenkosten-calculator" element={<HorseCostCalculatorPage />} />
              <Route path="/farrier-schedule-calculator" element={<FarrierScheduleCalculatorPage />} />
              <Route path="/hoefsmid-planner" element={<FarrierScheduleCalculatorPage />} />
              <Route path="/trailer-weight-calculator" element={<TrailerWeightCalculatorPage />} />
              <Route path="/trailer-gewicht-calculator" element={<TrailerWeightCalculatorPage />} />
              <Route path="/share/:token" element={<ToolSharePage />} />
              <Route path="/trending" element={<TopSharedPage />} />
              <Route path="/trending-analyses" element={<TopSharedPage />} />
              <Route path="/publisher/claim/:slug" element={<ClaimAppPage />} />
              <Route path="/publisher/verify/:token" element={<PublisherVerifyPage />} />
              <Route path="/publisher/dashboard" element={<PublisherDashboardPage />} />
              <Route path="/publisher/login" element={<PublisherLoginPage />} />
              <Route path="/best-horse-apps" element={<BestHorseAppsPage language="en" />} />
              <Route path="/beste-paardenapps" element={<BestHorseAppsPage language="nl" />} />
              <Route path="/best/:topic" element={<BestOfPage />} />
              <Route path="/versus/:matchup" element={<VersusPage />} />
              <Route path="/compare/:matchup" element={<VersusPage />} />
              <Route path="/advert-submit" element={<AdvertSubmitPage />} />
              <Route path="/claim/:token" element={<ClaimEditorPage />} />
              <Route path="/billing" element={<BillingPortalPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/queue" element={<BlogQueuePage />} />
              <Route path="/blog/new" element={<BlogGeneratePage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
            </Routes>
            <Footer />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </CompareProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
